/**
 * Empty state message component for marketplace.
 * Shows different messages based on why no campaigns could be fetched.
 */

import { Link } from "@tanstack/react-router";

export type EmptyReason =
  | "no_profiles"
  | "no_email"
  | "no_channels"
  | "no_complete_profile"
  | "fetch_error"
  | null;

interface EmptyStateMessageProps {
  emptyReason?: EmptyReason;
  hasActiveFilters?: boolean;
  orgSlug?: string;
}

export function EmptyStateMessage({
  emptyReason,
  hasActiveFilters,
  orgSlug,
}: EmptyStateMessageProps) {
  switch (emptyReason) {
    case "no_profiles":
      return (
        <div className="space-y-2">
          <p className="text-muted-foreground font-medium">No profiles found</p>
          <p className="text-sm text-muted-foreground">
            Create a profile to start browsing campaigns.
          </p>
        </div>
      );
    case "no_email":
      return (
        <div className="space-y-2">
          <p className="text-muted-foreground font-medium">Email required</p>
          <p className="text-sm text-muted-foreground">
            Add an email address to your profile to browse campaigns.
          </p>
        </div>
      );
    case "no_channels":
      return (
        <div className="space-y-2">
          <p className="text-muted-foreground font-medium">
            Connect your social accounts
          </p>
          <p className="text-sm text-muted-foreground">
            Link at least one social media account to your profile to see
            campaigns.
          </p>
          <p className="text-sm text-muted-foreground">
            Go to{" "}
            {orgSlug ? (
              <Link
                to="/o/$orgSlug/settings"
                params={{ orgSlug }}
                search={{ tab: "profiles" }}
                className="underline"
              >
                Profiles
              </Link>
            ) : (
              "Profiles"
            )}{" "}
            and select a profile, then click &quot;Social Accounts&quot;.
          </p>
        </div>
      );
    case "no_complete_profile":
      return (
        <div className="space-y-2">
          <p className="text-muted-foreground font-medium">
            Complete your profile
          </p>
          <p className="text-sm text-muted-foreground">
            Your profile needs both an email address and at least one connected
            social account to browse campaigns.
          </p>
        </div>
      );
    case "fetch_error":
      return (
        <div className="space-y-2">
          <p className="text-muted-foreground font-medium">
            Unable to load campaigns
          </p>
          <p className="text-sm text-muted-foreground">
            There was a problem fetching campaigns. Please try again later.
          </p>
        </div>
      );
    default:
      if (hasActiveFilters) {
        return (
          <p className="text-muted-foreground">
            No campaigns match those filters right now — try adjusting your
            search to discover more opportunities.
          </p>
        );
      }
      return (
        <p className="text-muted-foreground">
          No campaigns available at this time.
        </p>
      );
  }
}
